import React from 'react';
import PropTypes from 'prop-types';
import { MessageSquare } from 'lucide-react';
import { VERDICTS, VERDICT_CLASSES, VERDICT_DISPLAY } from '../constants';

// Helper functions for text formatting
const formatListItems = (paragraph, isFirstParagraph, index) => {
  const listItems = paragraph.split(/\n/).filter(item => item.trim());
  return (
    <ul key={index} className={`list-disc pl-5 space-y-1.5 ${isFirstParagraph ? 'mt-0.5 mb-3' : 'my-3'}`}>
      {listItems.map((item, j) => {
        // eslint-disable-next-line no-useless-escape
        const cleanItem = item.replace(/^[•\-\*]\s+/, '');
        return <li key={j} className="text-sm leading-relaxed">{cleanItem}</li>;
      })}
    </ul>
  );
};

const formatVerdictPhrase = (paragraph, index) => {
  // Check if it's an exact match for a verdict phrase
  if (
    paragraph === VERDICTS.GREAT_INSIGHT || 
    paragraph === VERDICTS.GOOD_INSIGHT || 
    paragraph === VERDICTS.NEEDS_IMPROVEMENT
  ) {
    return (
      <h3 key={index} className="text-sm font-semibold text-orange-300 mt-3 mb-2">
        {paragraph}
      </h3>
    );
  }
  return null;
};

const highlightVerdictPhrases = (text) => {
  // Create a regex to find any of the verdict phrases
  const verdictValues = Object.values(VERDICTS);
  const verdictRegex = new RegExp(`(${verdictValues.join('|')})`, 'g');
  
  // Split the text by the verdict phrases
  const parts = text.split(verdictRegex);
  
  // Find all matches to insert as highlighted spans
  const matches = text.match(verdictRegex) || [];
  
  // Create the result with highlighted verdict phrases
  const result = [];
  parts.forEach((part, index) => {
    if (part) result.push(part);
    if (index < matches.length) {
      result.push(
        <span key={`verdict-${index}`} className="text-orange-300 font-semibold">
          {matches[index]}
        </span>
      );
    }
  });
  
  return result;
};

const formatColonPhrase = (paragraph, index) => {
  const parts = paragraph.split(':');
  
  // Only apply special styling if the first part looks like a natural conversation transition
  if (parts.length > 1 && parts[0].trim().length < 50) {
    const firstPart = parts[0].trim();
    const restOfText = parts.slice(1).join(':').trim();
    
    return (
      <div key={index} className="mb-3 mt-1">
        <p className="text-sm">
          <span className="font-semibold text-white">{firstPart}:</span>
          <span className="ml-1">{restOfText}</span>
        </p>
      </div>
    );
  }
  return null;
};

const formatEmphasisText = (paragraph, index) => {
  if (/^[A-Z\s]{4,}/.test(paragraph)) {
    return (
      <p key={index} className="text-sm font-semibold text-white mt-4 mb-2">
        {paragraph}
      </p>
    );
  }
  return null;
};

const Message = ({ message, isUser }) => {
  // Function to format message text with paragraphs and line breaks
  const formatMessageText = (text) => {
    if (!text) return '';
    
    // Split by double newlines for paragraphs
    const paragraphs = text.split(/\n\n+/);
    
    return paragraphs.map((paragraph, i) => {
      // Handle lists with bullet points
      if (paragraph.includes('• ') || paragraph.includes('- ') || paragraph.includes('* ')) {
        return formatListItems(paragraph, i === 0, i);
      }
      
      // Handle exact verdict phrases
      const verdictPhrase = formatVerdictPhrase(paragraph, i);
      if (verdictPhrase) return verdictPhrase;
      
      // Handle verdict phrases within text
      if (
        paragraph.includes(VERDICTS.GREAT_INSIGHT) || 
        paragraph.includes(VERDICTS.GOOD_INSIGHT) || 
        paragraph.includes(VERDICTS.NEEDS_IMPROVEMENT)
      ) {
        return (
          <p key={i} className="leading-relaxed mb-2 text-sm">
            {highlightVerdictPhrases(paragraph)}
          </p>
        );
      }
      
      // Handle conversation transitions with colons
      if (paragraph.includes(':')) {
        const colonPhrase = formatColonPhrase(paragraph, i);
        if (colonPhrase) return colonPhrase;
      }
      
      // Handle emphasis text (ALL CAPS)
      const emphasisText = formatEmphasisText(paragraph, i);
      if (emphasisText) return emphasisText;
      
      // Handle regular paragraphs
      return (
        <p key={i} className="leading-relaxed mb-2 text-sm">
          {paragraph}
        </p>
      );
    });
  };

  // Render verdict badge based on verdict type
  const renderVerdictBadge = () => {
    const { verdict } = message;
    
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${VERDICT_CLASSES[verdict].badge}`}>
        {verdict === VERDICTS.GREAT_INSIGHT && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L14.4 9.2H22L16 13.8L18.4 21L12 16.4L5.6 21L8 13.8L2 9.2H9.6L12 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {verdict === VERDICTS.GOOD_INSIGHT && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {verdict === VERDICTS.NEEDS_IMPROVEMENT && (
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        {VERDICT_DISPLAY[verdict]}
      </span>
    );
  };

  return (
    <div className={`mb-5 animate-fadeIn ${isUser ? 'pl-6' : 'pr-6'}`}>
      <div className={`flex items-start gap-2.5 ${isUser ? 'justify-end' : 'justify-start'}`}>
        {!isUser && (
          <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden shadow-sm border border-gray-700/40 bg-gray-800 flex items-center justify-center">
            <img 
              src="/ai-avatar.png" 
              alt="AI Avatar" 
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to the gradient background with sparkles if image fails to load
                e.target.style.display = 'none';
                e.target.parentNode.classList.add('bg-gradient-to-br', 'from-orange-400', 'to-orange-600');
                const sparkleEl = document.createElement('div');
                sparkleEl.innerHTML = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="white">
                  <path d="M12 3V3.75M18.3955 5.6045L17.8695 6.1305M21 12H20.25M18.3955 18.3955L17.8695 17.8695M12 20.25V21M5.6045 18.3955L6.1305 17.8695M3.75 12H3M5.6045 5.6045L6.1305 6.1305M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>`;
                e.target.parentNode.appendChild(sparkleEl);
              }}
            />
          </div>
        )}
        <div className={`rounded-xl px-4 shadow-md max-w-3xl ${
          isUser
            ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white self-end py-2 border border-blue-300/40'
            : 'bg-gradient-to-r from-gray-700/80 to-gray-800/90 backdrop-blur-sm text-gray-100 border border-gray-500/50 pt-3 pb-2'
        }`}>
          <div className={`whitespace-pre-line ${!isUser ? 'pt-1.5 -mt-1' : ''}`}>
            {isUser ? (
              <p className="leading-relaxed text-sm">{message.text}</p>
            ) : (
              formatMessageText(message.text)
            )}
          </div>
          
          {!isUser && message.verdict && (
            <div className={`mt-2 pt-2 border-t ${VERDICT_CLASSES[message.verdict].border}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                {renderVerdictBadge()}
                
                {/* Show model info if using fallback model */}
                {message.modelUsed && !message.modelUsed.includes('sonnet-4-6') && (
                  <div className="flex flex-col">
                    <span className="text-[10px] text-amber-400/80 transition-opacity hover:opacity-100 opacity-80 cursor-help flex items-center gap-1 animate-pulse" 
                          title="We're experiencing high demand. During this time, responses may be less accurate or nuanced than usual.">
                      <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M12 8v4"/>
                        <path d="M12 16h.01"/>
                      </svg>
                      High demand mode: Reduced accuracy
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {isUser && (
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-white shadow-sm flex items-center justify-center">
            <MessageSquare size={16} />
          </div>
        )}
      </div>
    </div>
  );
};

Message.propTypes = {
  message: PropTypes.shape({
    text: PropTypes.string.isRequired,
    verdict: PropTypes.string,
    modelUsed: PropTypes.string,
    isUser: PropTypes.bool
  }).isRequired,
  isUser: PropTypes.bool.isRequired
};

export default Message;